import { HubStatus, SensorStatus } from "common";
import { useTranslation } from "react-i18next";
import { object, ref, string } from "yup";

export const useFieldValidationSchema = () => {
  const { t } = useTranslation();

  const nameSchema = string()
    .required(t("validation.nameRequired"))
    .min(3, t("validation.nameMinNumOfCharacters", { count: 3 }));

  const emailSchema = string()
    .required(t("validation.emailRequired"))
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      t("validation.emailIncorrectFormat"),
    );

  const passwordLoginSchema = string()
    .required(t("validation.passwordRequired"))
    .min(4, t("validation.passwordTooShort"));

  const passwordConfirmSchema = string().required(
    t("validation.passwordRequired"),
  );

  const passwordRegisterSchema = string()
    .required(t("validation.passwordRequired"))
    .min(8, t("validation.passwordTooShort"))
    .test("isValidPassword", t("validation.passwordNotValid"), (value) => {
      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumber = /[0-9]/.test(value);
      const hasSpecialCharacter = /[^a-zA-Z0-9]/.test(value);

      const conditions = [
        hasLowerCase,
        hasUpperCase,
        hasNumber,
        hasSpecialCharacter,
      ];
      const isValid = conditions.every((condition) => condition === true);

      return isValid;
    });

  const totpSchema = string()
    .required(t("validation.totpRequired"))
    .length(6, t("validation.totpLength", { count: 6 }));

  const baseStationFormValidationSchema = object({
    name: string().required(t("baseStations.form.nameRequired")),
    serialNumber: string().required(t("baseStations.form.serialRequired")),
    macAddress: string()
      .matches(/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/, {
        message: t("baseStations.form.macAddressInvalidFormat"),
      })
      .required(t("baseStations.form.macAddressRequired")),
    status: string()
      .oneOf([
        HubStatus.Active,
        HubStatus.Inactive,
        HubStatus.Maintenance,
        HubStatus.Pending,
      ])
      .required(t("baseStations.form.statusRequired")),
  });

  const sensorFormValidationSchema = object({
    name: string().required(t("sensors.form.nameRequired")),
    location: string().required(t("sensors.form.locationRequired")),
    serialNumber: string().required(t("sensors.form.serialRequired")),
    dsk: string()
      .matches(/^\d{5}(?:-\d{5}){7}$/, {
        message: t("sensors.form.dskInvalidFormat"),
      })
      .required(t("sensors.form.dskRequired")),
    type: string()
      .oneOf([
        "TEMPERATURE",
        "HUMIDITY",
        "PRESSURE",
        "LIGHT",
        "CO2",
        "PM25",
        "PM10",
        "VOC",
        "LEAK_DETECTOR",
        "HVAC_CONDENSER_MONITOR",
        "HVAC_AIRHANDLER_MONITOR",
        "HVAC_AIRHANDLER_MONITOR_CT",
        "HVAC_CONDENSER_MONITOR_CT",
        "POWER_MONITOR",
        "AIR_QUALITY",
        "WATER_QUALITY",
        "OTHER",
      ])
      .required(t("sensors.form.typeRequired")),
    version: string()
      .matches(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/, {
        message: t("sensors.form.versionInvalidFormat"),
      })
      .required(t("sensors.form.versionRequired")),
    status: string()
      .oneOf([
        SensorStatus.Active,
        SensorStatus.Inactive,
        SensorStatus.Maintenance,
        SensorStatus.Pending,
      ])
      .required(t("sensors.form.statusRequired")),
  });

  const loginValidationSchema = object({
    email: emailSchema,
    password: passwordLoginSchema,
  });

  const forgotPasswordValidationSchema = object({
    email: emailSchema,
  });

  const resetPasswordValidationSchema = object({
    email: emailSchema,
    code: string()
      .required(t("validation.resetCodeRequired"))
      .length(6, t("validation.resetCodeLength", { count: 6 })),
    newPassword: passwordRegisterSchema,
    repeatedNewPassword: passwordConfirmSchema.oneOf(
      [ref("newPassword")],
      t("validation.passwordsDoNotMatch"),
    ),
  });

  const requestPasswordChangeValidationSchema = object({
    name: nameSchema,
    newPassword: passwordRegisterSchema,
    repeatedNewPassword: passwordConfirmSchema.oneOf(
      [ref("newPassword")],
      t("validation.passwordsDoNotMatch"),
    ),
  });

  const generalSettingsValidationSchema = object({
    street: string().required(),
    city: string().required(),
    state: string().required(),
    zip: string().required(),
    date: string().optional(),
    time: string().optional(),
    timezone: string().optional(),
  });

  const totpSetupValidationSchema = object({
    code: totpSchema,
  });

  return {
    baseStationFormValidationSchema,
    sensorFormValidationSchema,
    loginValidationSchema,
    forgotPasswordValidationSchema,
    resetPasswordValidationSchema,
    requestPasswordChangeValidationSchema,
    generalSettingsValidationSchema,
    totpSetupValidationSchema,
  };
};

export default useFieldValidationSchema;
